import * as Ariakit from '@ariakit/react'
import classNames from 'classnames'
import * as React from 'react'

import { BUTTON_DEFAULT_ELEMENT, Button, ButtonComponentProps, ButtonPadding, ButtonThemeType } from '../button'

interface InternalOptionProps {
  /**
   * Description for the option
   */
  subtitle?: React.ReactNode

  /**
   * Set the button theme when it is selected
   */
  activeTheme?: ButtonThemeType

  /**
   * Set the base button theme when not selected
   */
  theme?: ButtonThemeType | ((isHovered: boolean) => ButtonThemeType)
}

function getDefaultTheme(isHovered: boolean) {
  return isHovered ? 'menuItem' : 'menuItemTransparent'
}

const InternalOption = React.forwardRef(function InternalOption<
  TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT,
>(
  {
    subtitle,
    children,
    'aria-selected': ariaSelected,
    'data-active-item': dataActiveItem,
    rounding = 'small',
    activeTheme = 'blue',
    theme = getDefaultTheme,
    ...props
  }: Omit<ButtonComponentProps<TagType>, 'theme'> & InternalOptionProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const isSelected = ariaSelected === true
  const isHovered = dataActiveItem === true
  const defaultTheme = typeof theme === 'function' ? theme(isHovered) : theme

  return (
    <Button
      // TS thinks the Omit type (due to spread above) doesn’t satisfy button props
      {...(props as ButtonComponentProps<TagType>)}
      ref={ref}
      align="left"
      className={classNames(props.className, ButtonPadding.regular, 'select-none', {
        'text-2xs': !!subtitle && !props.theme,
      })}
      contentClass={classNames(props.contentClass, 'font-normal')}
      rounding={rounding}
      active={props.active ?? (isHovered && !isSelected)}
      theme={isSelected ? activeTheme : defaultTheme}
      size={props.size ?? (subtitle ? 'none' : props.size)}
      aria-selected={ariaSelected}
      data-active-item={dataActiveItem}
    >
      {children}
      {subtitle && (
        <div
          className={classNames('-mt-1 w-full whitespace-normal text-3xs', {
            'text-muted-2': !isSelected,
          })}
        >
          {subtitle}
        </div>
      )}
    </Button>
  )
})

interface SelectOptionProps<ValueType> extends InternalOptionProps {
  value: ValueType
}

export const SelectOption = React.forwardRef(function SelectOption<
  ValueType,
  TagType extends React.ElementType = typeof BUTTON_DEFAULT_ELEMENT,
>(
  { as, ...props }: Omit<ButtonComponentProps<TagType>, 'value' | 'theme'> & SelectOptionProps<ValueType>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const context = Ariakit.useSelectContext()!
  const isComboBox = !!context.combobox

  return (
    <Ariakit.SelectItem
      ref={ref}
      {...props}
      render={isComboBox ? <Ariakit.ComboboxItem render={<InternalOption as={as} />} /> : <InternalOption as={as} />}
    />
  )
})
